import { useEffect, useRef, useState } from "react";
import { Loader2, Check, AlertTriangle, X } from "lucide-react";
import {
  progressiveReveal,
  type OCRResultLike,
  type OCRFieldResult,
  type OCRDataField,
  type BadgeState,
} from "@/lib/ocr-progressive-timing";

type ColorScheme = "azul" | "emerald";

type Props = {
  result: OCRResultLike & { confianca_global?: number };
  colorScheme: ColorScheme;
  onComplete: (badge: BadgeState) => void;
};

type RevealedState = {
  valor?: OCRFieldResult;
  terminal?: OCRFieldResult;
  data?: OCRDataField;
};

const colorTokens = {
  azul: {
    matchIcon: "text-emerald-600",
    badgeSuccess: "bg-ep-primary-container text-ep-primary",
  },
  emerald: {
    matchIcon: "text-emerald-700",
    badgeSuccess: "bg-emerald-100 text-emerald-900",
  },
} as const;

function formatCurrency(v: number | string | null | undefined): string {
  if (v === null || v === undefined) return "—";
  const n = typeof v === "string" ? parseFloat(v) : v;
  if (isNaN(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function FieldRow({
  label,
  revealed,
  value,
  match,
  expected,
  scheme,
}: {
  label: string;
  revealed: boolean;
  value: string;
  /** Present for valor/terminal rows (has match concept). Absent for data row. */
  match?: boolean;
  expected?: string;
  scheme: ColorScheme;
}) {
  const t = colorTokens[scheme];
  return (
    <div className="space-y-1">
      <div className="text-xs uppercase tracking-wide font-medium text-ep-on-surface-variant">
        {label}
      </div>
      {!revealed ? (
        <div
          aria-label={`skeleton-${label.toLowerCase().split(" ")[0]}`}
          className="h-7 w-32 bg-muted rounded animate-pulse"
        />
      ) : (
        <div className="flex items-center gap-2 transition-all duration-300 ease-out">
          <span className={`text-lg tabular-nums ${match === false ? "text-red-600" : ""}`}>
            {value}
          </span>
          {match === true && <Check className={`h-4 w-4 ${t.matchIcon}`} />}
          {match === false && <X className="h-4 w-4 text-red-600" />}
          {value === "—" && match !== true && <AlertTriangle className="h-4 w-4 text-amber-600" />}
        </div>
      )}
      {revealed && match === false && expected && (
        <div className="text-xs text-ep-on-surface-variant">Você informou: {expected}</div>
      )}
    </div>
  );
}

export default function OCRProgressiveReceipt({ result, colorScheme, onComplete }: Props) {
  const [revealed, setRevealed] = useState<RevealedState>({});
  const [badge, setBadge] = useState<BadgeState | null>(null);

  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    const cleanup = progressiveReveal(
      result,
      (field, value) => setRevealed((prev) => ({ ...prev, [field]: value })),
      (b) => {
        setBadge(b);
        onCompleteRef.current(b);
      }
    );
    return cleanup;
  }, [result]);

  const t = colorTokens[colorScheme];

  return (
    <div data-color-scheme={colorScheme} className="space-y-6 p-6">
      <div className="flex items-center gap-3">
        {badge === null ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin text-ep-on-surface-variant" />
            <h3 className="text-lg font-semibold">Validando comprovante…</h3>
          </>
        ) : (
          <h3 className="text-lg font-semibold">Validação concluída</h3>
        )}
      </div>

      <div className="space-y-4">
        <FieldRow
          label="VALOR"
          revealed={!!revealed.valor}
          value={formatCurrency(revealed.valor?.encontrado ?? null)}
          match={revealed.valor !== undefined ? revealed.valor.match : undefined}
          expected={
            revealed.valor?.esperado != null ? formatCurrency(revealed.valor.esperado) : undefined
          }
          scheme={colorScheme}
        />
        <FieldRow
          label="TERMINAL"
          revealed={!!revealed.terminal}
          value={String(revealed.terminal?.encontrado ?? "—")}
          match={revealed.terminal !== undefined ? revealed.terminal.match : undefined}
          expected={
            revealed.terminal?.esperado ? String(revealed.terminal.esperado) : undefined
          }
          scheme={colorScheme}
        />
        {result.data !== undefined && (
          <FieldRow
            label="DATA / HORA"
            revealed={!!revealed.data}
            value={String(revealed.data?.encontrado ?? "—")}
            scheme={colorScheme}
          />
        )}
      </div>

      {badge && (
        <div
          className={`text-sm font-medium px-4 py-3 rounded-md ${
            badge === "success"
              ? t.badgeSuccess
              : badge === "warning"
                ? "bg-amber-50 text-amber-900"
                : "bg-red-50 text-red-900"
          }`}
        >
          {badge === "success" && "✓ Tudo certo!"}
          {badge === "warning" && "⚠ Algo divergiu — confira antes de enviar"}
          {badge === "error" && "✕ Não conseguimos ler — você pode enviar mesmo assim"}
        </div>
      )}
    </div>
  );
}
